import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { LanguageSelection } from '@/components/screens/LanguageSelection'
import { AvatarSelection } from '@/components/screens/AvatarSelection'
import { useJoinStore } from '@/store/joinStore'
import { selectSession } from '@/api/publicSession'
import type { Language } from '@/types/join'

type Step = 'language' | 'avatar' | 'submitting' | 'error'

export function PreferencesScreen() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const resolved = useJoinStore((s) => s.resolved)
  const bearer = useJoinStore((s) => s.bearer)
  const setSelected = useJoinStore((s) => s.setSelected)

  const availableLanguages = resolved?.candidate.availableLanguages ?? []
  const avatars = resolved?.avatar.avatars ?? []

  // Defensive redirect — Resolver should hydrate the store before navigating here.
  useEffect(() => {
    if (!resolved || !bearer || !sessionId) {
      navigate(`/j/${sessionId ?? ''}`, { replace: true })
    }
  }, [resolved, bearer, sessionId, navigate])

  // Auto-pick single options so we land directly on whichever step has a choice.
  const initialLanguage: Language | null =
    availableLanguages.length === 1 ? (availableLanguages[0] as Language) : null
  const initialIndex: number | null =
    avatars.length === 1 ? avatars[0].index : null

  const [language, setLanguage] = useState<Language | null>(initialLanguage)
  const [avatarIndex, setAvatarIndex] = useState<number | null>(initialIndex)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const step: Step =
    language === null ? 'language' :
    avatarIndex === null ? 'avatar' :
    errorMessage ? 'error' : 'submitting'

  // Submit when both are set.
  useEffect(() => {
    if (step !== 'submitting' || !sessionId || !bearer || !language || avatarIndex === null) return
    void (async () => {
      try {
        await selectSession(sessionId, bearer, {
          selectedLanguage: language,
          selectedAvatarIndex: avatarIndex,
        })
        setSelected(language, avatarIndex)
        navigate(`/j/${sessionId}/setup`, { replace: true })
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : String(err))
        if (availableLanguages.length > 1) setLanguage(null)
        if (avatars.length > 1) setAvatarIndex(null)
      }
    })()
  }, [step, sessionId, bearer, language, avatarIndex, setSelected, navigate, availableLanguages.length, avatars.length])

  if (!resolved || !bearer || !sessionId) return null

  if (step === 'language') {
    return <LanguageSelection onSelect={(lang) => setLanguage(lang)} />
  }
  if (step === 'avatar') {
    return (
      <AvatarSelection
        language={language ?? 'de'}
        avatars={avatars}
        onSelect={(i) => setAvatarIndex(i)}
        onBack={availableLanguages.length > 1 ? () => setLanguage(null) : undefined}
      />
    )
  }
  if (step === 'error') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-lg">Auswahl konnte nicht gespeichert werden.</p>
        <p className="text-sm text-neutral-400">{errorMessage}</p>
        <button
          onClick={() => { setErrorMessage(null); setLanguage(initialLanguage); setAvatarIndex(initialIndex) }}
          className="rounded bg-neutral-800 px-4 py-2"
        >
          Erneut versuchen
        </button>
      </div>
    )
  }
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-700 border-t-white" />
    </div>
  )
}
